/**
 * Promise-based confirm dialog using DaisyUI modal.
 * Drop-in async replacement for `window.confirm()`.
 *
 * Accessibility:
 *  - role="alertdialog" + aria-labelledby/aria-describedby
 *  - focus is moved to the primary action on open
 *  - focus is trapped within the modal (Tab / Shift+Tab cycle)
 *  - ESC dismisses (resolves false)
 *  - focus is restored to the previously active element on close
 */
export function confirmDialog(
  message: string,
  options?: { title?: string; confirmLabel?: string; cancelLabel?: string; confirmClass?: string },
): Promise<boolean> {
  const { title = 'Confirm', confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmClass = 'btn-error' } = options ?? {};

  return new Promise<boolean>(resolve => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open';
    overlay.style.zIndex = '10000';

    const titleId = `cd-title-${Math.random().toString(36).slice(2, 9)}`;
    const descId = `cd-desc-${Math.random().toString(36).slice(2, 9)}`;

    const box = document.createElement('div');
    box.className = 'modal-box';
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', titleId);
    box.setAttribute('aria-describedby', descId);

    const h3 = document.createElement('h3');
    h3.id = titleId;
    h3.className = 'text-lg font-bold';
    h3.textContent = title;

    const p = document.createElement('p');
    p.id = descId;
    p.className = 'py-4 text-sm';
    p.textContent = message;

    const actions = document.createElement('div');
    actions.className = 'modal-action';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn-ghost';
    cancelBtn.textContent = cancelLabel;

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = `btn ${confirmClass}`;
    confirmBtn.textContent = confirmLabel;

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    box.appendChild(h3);
    box.appendChild(p);
    box.appendChild(actions);

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    overlay.appendChild(box);
    overlay.appendChild(backdrop);

    const focusables = [cancelBtn, confirmBtn];

    const cleanup = (result: boolean) => {
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      // Restore focus to whatever was focused before the modal opened
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch { /* ignore */ }
      }
      resolve(result);
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        cleanup(false);
        return;
      }
      if (ev.key === 'Tab') {
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (ev.shiftKey && active === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && active === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    cancelBtn.addEventListener('click', () => cleanup(false));
    confirmBtn.addEventListener('click', () => cleanup(true));
    backdrop.addEventListener('click', () => cleanup(false));
    document.addEventListener('keydown', onKeydown, true);

    document.body.appendChild(overlay);

    // Autofocus the primary action on next frame (after layout)
    requestAnimationFrame(() => confirmBtn.focus());
  });
}

/**
 * Like {@link confirmDialog}, but with one opt-in checkbox below the message.
 * Resolves `{ confirmed, checked }` — `confirmed` is the primary action, and
 * `checked` is the box's final state (only meaningful when confirmed). Cancel,
 * ESC and the backdrop resolve `{ confirmed: false, checked: false }`.
 */
export function confirmDialogWithCheckbox(
  message: string,
  options: {
    checkboxLabel: string;
    checkboxDefault?: boolean;
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmClass?: string;
  },
): Promise<{ confirmed: boolean; checked: boolean }> {
  const {
    checkboxLabel,
    checkboxDefault = false,
    title = 'Confirm',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmClass = 'btn-error',
  } = options;

  return new Promise(resolve => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open';
    overlay.style.zIndex = '10000';

    const titleId = `cd-title-${Math.random().toString(36).slice(2, 9)}`;
    const descId = `cd-desc-${Math.random().toString(36).slice(2, 9)}`;

    const box = document.createElement('div');
    box.className = 'modal-box';
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', titleId);
    box.setAttribute('aria-describedby', descId);

    const h3 = document.createElement('h3');
    h3.id = titleId;
    h3.className = 'text-lg font-bold';
    h3.textContent = title;

    const p = document.createElement('p');
    p.id = descId;
    p.className = 'pt-4 text-sm';
    p.textContent = message;

    const optionLabel = document.createElement('label');
    optionLabel.className = 'flex cursor-pointer items-start gap-2 py-4 text-sm';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'checkbox checkbox-sm mt-0.5';
    checkbox.checked = checkboxDefault;
    const optionText = document.createElement('span');
    optionText.textContent = checkboxLabel;
    optionLabel.appendChild(checkbox);
    optionLabel.appendChild(optionText);

    const actions = document.createElement('div');
    actions.className = 'modal-action';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn-ghost';
    cancelBtn.textContent = cancelLabel;

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = `btn ${confirmClass}`;
    confirmBtn.textContent = confirmLabel;

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    box.appendChild(h3);
    box.appendChild(p);
    box.appendChild(optionLabel);
    box.appendChild(actions);

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    overlay.appendChild(box);
    overlay.appendChild(backdrop);

    const focusables = [checkbox, cancelBtn, confirmBtn];

    const cleanup = (confirmed: boolean) => {
      document.removeEventListener('keydown', onKeydown, true);
      const checked = confirmed && checkbox.checked;
      overlay.remove();
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch { /* ignore */ }
      }
      resolve({ confirmed, checked });
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        cleanup(false);
        return;
      }
      if (ev.key === 'Tab') {
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (ev.shiftKey && active === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && active === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    cancelBtn.addEventListener('click', () => cleanup(false));
    confirmBtn.addEventListener('click', () => cleanup(true));
    backdrop.addEventListener('click', () => cleanup(false));
    document.addEventListener('keydown', onKeydown, true);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => confirmBtn.focus());
  });
}
